import { MessageDialog } from '@bryntum/schedulerpro';
import React, { createContext, useContext, useState, useRef, ReactNode, useMemo, useCallback } from 'react';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';
import { GRANTS } from '@/other/constants/PrivilegeTypes';
import { showConfirmationDialog } from '@/other/utils/utils';
import { viewInspectorSchema } from '@/react/schema/viewInspectorSchema';
import { useSwivuForm } from '@/react/hooks/useSwivuForm';
import { ViewEditorProps } from '../ViewEditor';
import { View } from '@/bryntum-lib/models/View';
import useViews from '@/react/hooks/useViews';
import { useSwivuStore } from '@/react/hooks/useSwivuStore';
import { STORE_KEYS } from '@/other/constants/StoreKeys';
import { PERSISTENCE_STATE_KEYS } from '@/other/constants/PersistenceStateKeys';

interface ViewEditorContextType extends Omit<ViewEditorProps, 'record'> {
  viewName: string;
  setViewName: (name: string) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  onSaveView: () => void;
  onSaveViewAs: () => void;
  onRemoveView: () => void;
  form: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  formState: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  isDeleteDisabled: boolean;
  isSaveDisabled: boolean;
  isSaveAsView?: boolean;
  canUseRecordViewState?: boolean;
  record: View;
  isShareable: boolean;
  activeTab: string;
  setActiveTab: (value: string) => void;
  focusViewNameRef: React.MutableRefObject<(() => void) | null>;
}

const ViewEditorContext = createContext<ViewEditorContextType | undefined>(undefined);

interface ViewEditorProviderProps extends ViewEditorProps {
  children: ReactNode;
}

export const ViewEditorProvider: React.FC<ViewEditorProviderProps> = (props: ViewEditorProviderProps) => {
  const context = useSwivuBryntumContext();
  const viewManagerState = useViews();
  const [viewName, setViewName] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('view-details');
  const focusViewNameRef = useRef<(() => void) | null>(null);
  const { record: editorRecord, ...otherProps } = props;
  const { onClose } = otherProps;
  const {version: pittsVersion} = useSwivuStore({
    storeId: STORE_KEYS.userPreferencesStore,
    // @ts-expect-error - key is not typed
    subscriberFn: ({ record: changedRecord }) => changedRecord?.key === PERSISTENCE_STATE_KEYS.viewStatePIITS
  });

  const { canUseRecordViewState, isSaveAsView } = editorRecord;
  const record = viewManagerState?.selectedView;

  const { form, formState } = useSwivuForm({
    schema: viewInspectorSchema,
    record: record,
    updateRecordOnFormValueChange: false,
    version: "",
    defaultValues: {
      name: record.name || "",
      viewDescription: record.viewDescription || "",
      folderId: record.folderId,
      iconId: record.iconId,
      color: record.color || null,
      isShareToHQ: record.isShareToHQ || false,
      isShareToWorkforce: record.isShareToWorkforce || false,
      isShareToPublic: record.isShareToPublic || false
    }
  });

  const name = form.watch("name");

  const handleSaveButtonState = useCallback(() => {
    const selectedView = record;
    const haveFieldsChanged = formState.isDirty;

    const isViewNameEmpty = !name?.trim();
    return isViewNameEmpty || !((selectedView.isOwnView && selectedView.hasPIITS) || haveFieldsChanged);
  }, [name, record, formState, pittsVersion]);

  const isDefault = record.isDefaultView;
  const isAdmin = context.user?.can(GRANTS.grant_one) ?? false;
  const isOwn = record.isOwnView;
  const isShareable = !(isSaveAsView || isAdmin);
  const isDeleteDisabled = isDefault || !(isAdmin || isOwn);
  const isSaveDisabled = useMemo(() => handleSaveButtonState(), [handleSaveButtonState]);


  const onUnShareSomeoneElseView = (recordToUpdate: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const { project, viewManager } = context;
    project.on({
      sync: () => {
        if (!recordToUpdate.isOwnView) {
          const dataToApply = { removed: [{ id: recordToUpdate.id }] };
          project.viewsStore.applyChangeset(dataToApply);
          viewManager?.selectDefaultViewOfCurrentView();
        }
      },
      once: true
    });
  };

  const handleRecordUpdate = (recordToUpdate: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const values = form.getValues();
    recordToUpdate.name = values.name;
    recordToUpdate.iconId = values.iconId;
    recordToUpdate.color = values.color;
    recordToUpdate.folderId = values.folderId;
    recordToUpdate.isShareToHQ = values.isShareToHQ;
    recordToUpdate.isShareToPublic = values.isShareToPublic;
    recordToUpdate.isShareToWorkforce = values.isShareToWorkforce;
    recordToUpdate.viewDescription = values.viewDescription;

    const shareValues = [
      recordToUpdate.isShareToHQ,
      recordToUpdate.isShareToPublic,
      recordToUpdate.isShareToWorkforce
    ];
    if (shareValues.filter((val) => val).length === 0 && !recordToUpdate.isOwnView) {
      onUnShareSomeoneElseView(recordToUpdate);
    }
  };


  const onSaveView = () => {
    // @ts-expect-error - expect typescript error
    const isViewSelected = record.id === context.viewManager?.renderState?.selectedView?.id;
    let isViewUpdated = true;

    if (isSaveAsView) {
      onSaveViewAs();
    } else {
      handleRecordUpdate(record);
      if (record.isOwnView && isViewSelected) {
        // @ts-expect-error - viewManager is not null
        isViewUpdated = context.viewManager.updateView(record.id, record);
      }

      // Set higher z-index for MessageDialog to appear above modal
      const originalZIndex = MessageDialog.element?.style.zIndex;
      if (MessageDialog.element) {
        MessageDialog.element.style.zIndex = '99999';
      }

      if (isViewUpdated) {
        MessageDialog.alert({
          title: "Success",
          message: "View updated successfully."
        }).finally(() => {
          // Restore original z-index
          if (MessageDialog.element && originalZIndex !== undefined) {
            MessageDialog.element.style.zIndex = originalZIndex;
          }
        });
      } else {
        MessageDialog.alert({
          title: "Failed",
          message: "Unable to update the view."
        }).finally(() => {
          // Restore original z-index
          if (MessageDialog.element && originalZIndex !== undefined) {
            MessageDialog.element.style.zIndex = originalZIndex;
          }
        });
      }
    }
  };

  const onSaveViewAs = () => {
    const newRecord = record.copy();
    newRecord.isDefaultView = false;
    handleRecordUpdate(newRecord);
    const { viewManager } = context;
    const isViewCreated = viewManager?.createView(newRecord);

    // Set higher z-index for MessageDialog to appear above modal
    const originalZIndex = MessageDialog.element?.style.zIndex;
    if (MessageDialog.element) {
      MessageDialog.element.style.zIndex = '99999';
    }

    if (isViewCreated) {
      MessageDialog.alert({
        title: "Success",
        message: "View saved successfully."
      }).finally(() => {
        // Restore original z-index
        if (MessageDialog.element && originalZIndex !== undefined) {
          MessageDialog.element.style.zIndex = originalZIndex;
        }
        onClose();
      });
    } else {
      MessageDialog.alert({
        title: "Unable to save view.",
        message: "The name of the view is already in use. Please choose a different name."
      }).finally(() => {
        // Restore original z-index
        if (MessageDialog.element && originalZIndex !== undefined) {
          MessageDialog.element.style.zIndex = originalZIndex;
        }
        // Switch to General tab and focus view name input
        setActiveTab('view-details');
        setTimeout(() => focusViewNameRef.current?.(), 150);
      });
    }
  };

  const onRemoveView = () => {
    const user = context.user;
    const viewToDelete = record;
    if (viewToDelete) {
      if (viewToDelete.isDefaultView) {
        // Set higher z-index for MessageDialog to appear above modal
        const originalZIndex = MessageDialog.element?.style.zIndex;
        if (MessageDialog.element) {
          MessageDialog.element.style.zIndex = '99999';
        }
        MessageDialog.alert({
          title: "View Delete Not Allowed",
          message: "Swivu views cannot be deleted."
        }).finally(() => {
          // Restore original z-index
          if (MessageDialog.element && originalZIndex !== undefined) {
            MessageDialog.element.style.zIndex = originalZIndex;
          }
        });
      } else if (
        user &&
        viewToDelete.isSharedView &&
        !user.can(GRANTS.grant_one) &&
        viewToDelete.creatorUserId !== user.id
      ) {
        // Set higher z-index for MessageDialog to appear above modal
        const originalZIndex = MessageDialog.element?.style.zIndex;
        if (MessageDialog.element) {
          MessageDialog.element.style.zIndex = '99999';
        }
        MessageDialog.alert({
          title: "View Share Not Allowed",
          message: `Shared View created by ${viewToDelete.creatorUserName}. Only creator of this shared view or a board admin can delete this view.`
        }).finally(() => {
          // Restore original z-index
          if (MessageDialog.element && originalZIndex !== undefined) {
            MessageDialog.element.style.zIndex = originalZIndex;
          }
        });
      } else {
        // Set higher z-index for MessageDialog to appear above modal
        const originalZIndex = MessageDialog.element?.style.zIndex;
        if (MessageDialog.element) {
          MessageDialog.element.style.zIndex = '99999';
        }
        showConfirmationDialog({
          title: "Delete View",
          message: `Are you sure, You want to delete a view called "${viewToDelete.name}"?`,
          confirmBtnText: "Delete",
          cancelBtnText: "Cancel",
          isCloseByHeader: false
        }).then((isConfirmedDelete) => {
          // Restore original z-index
          if (MessageDialog.element && originalZIndex !== undefined) {
            MessageDialog.element.style.zIndex = originalZIndex;
          }
          if (isConfirmedDelete) {
            context.viewManager?.deleteView(viewToDelete);
            onClose();
          }
        });
      }
    }
  };

  if (!record) return null;

  return (
    <ViewEditorContext.Provider
      value={{
        viewName,
        setViewName,
        isEditing,
        setIsEditing,
        onSaveView,
        onSaveViewAs,
        onRemoveView,
        form,
        formState,
        isDeleteDisabled,
        isSaveDisabled,
        canUseRecordViewState,
        record,
        isShareable,
        activeTab,
        setActiveTab,
        focusViewNameRef,
        ...editorRecord,
        ...otherProps
      }}
    >
      {props.children}
    </ViewEditorContext.Provider>
  );
};

export const useViewEditor = () => {
  const context = useContext(ViewEditorContext);
  if (context === undefined) {
    throw new Error('useViewEditor must be used within a ViewEditorProvider');
  }
  return context;
};
